<template>
  <div>
    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">{{ $t('account.activeSessions') }}</h2>

        <v-card elevation="0" class="pa-4">
          <v-row class="align-center mb-2" density="comfortable">
            <v-col :cols="this.$vuetify.display.mdAndDown ? 12 : 7">
              <div class="text-body-2 text-medium-emphasis">
                {{ $t('account.activeSessionsDescription') }}
              </div>
            </v-col>

            <v-col
              :cols="this.$vuetify.display.mdAndDown ? 12 : 5"
              class="d-flex flex-wrap  gap-2"
              :class="{
                'justify-md-end': !this.$vuetify.display.mdAndDown,
              }"
            >
              <v-btn
                color="primary"
                variant="outlined"
                :loading="isLoading"
                :disabled="isActionDisabled"
                @click="loadSessions"
              >
                {{ $t('account.refreshSessions') }}
              </v-btn>

              <v-btn
                color="red-darken-3"
                variant="outlined"
                :loading="isTerminatingOthers"
                :disabled="isActionDisabled || terminableOtherSessionCount === 0"
                @click="openTerminateOthersDialog"
              >
                {{ $t('account.terminateOtherSessions') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-3">
            {{ errorMessage }}
          </v-alert>

          <div v-if="isLoading" class="account-sessions__loading py-8">
            <v-progress-circular color="primary" indeterminate :aria-label="$t('account.loadingSessions')"></v-progress-circular>
            <span class="ml-3">{{ $t('account.loadingSessions') }}</span>
          </div>

          <v-alert v-else-if="!hasSessions" type="info" variant="tonal">
            {{ $t('account.noActiveSessions') }}
          </v-alert>

          <div v-else>
            <h3 class="text-subtitle-1 font-weight-medium mb-2 ml-2">
              {{ $t('account.activeSessions') }}
            </h3>

            <v-list v-if="activeSessions.length > 0" class="account-sessions__list px-2 mb-4" lines="three">
              <v-list-item
                v-for="session in activeSessions"
                :key="session.sessionId"
                :class="{
                  'account-sessions__item': true,
                  'px-4': true,
                  'py-3': true,
                  'account-sessions__item--current': session.isCurrent,
                }"
              >
                <template v-slot:prepend>
                  <v-icon class="mr-2 mt-2">{{ getDeviceIcon(session) }}</v-icon>
                </template>

                <v-list-item-title>
                  {{ getDeviceName(session) }}
                  <v-chip v-if="session.isCurrent" color="primary" size="small" variant="tonal" class="ml-2 d-none d-lg-inline-flex">
                    {{ $t('account.currentSession') }}
                  </v-chip>
                </v-list-item-title>

                <v-list-item-subtitle>
                  <v-row density="comfortable">
                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionIpAddress') }}:</strong>
                      {{ session.ipAddress || $t('account.sessionUnknown') }}
                    </v-col>

                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionFirstSeen') }}:</strong>
                      {{ formatTimestamp(session.firstSeenAt) }}
                    </v-col>

                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionLastSeen') }}:</strong>
                      {{ formatTimestamp(session.lastSeenAt) }}
                    </v-col>

                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionExpires') }}:</strong>
                      {{ formatTimestamp(session.expiresAt) }}
                    </v-col>
                  </v-row>
                </v-list-item-subtitle>

                <template v-if="!session.isCurrent" v-slot:append>
                  <v-icon-btn
                    color="red-darken-3"
                    variant="outlined"
                    icon="mdi-close"
                    :loading="terminatingSessionId === session.sessionId"
                    :disabled="isActionDisabled"
                    @click="openTerminateDialog(session)"
                    class="mt-2"
                    width="32"
                    height="32"
                  />
                </template>
                <template v-else v-slot:append>
                  <!-- Display an empty space in where the revoke session button should be -->
                  <div style="width: 32px; height: 32px; display: block" />
                </template>
              </v-list-item>
            </v-list>

            <h3 class="text-subtitle-1 font-weight-medium mb-2 ml-2">
              {{ $t('account.previousSessions') }}
            </h3>

            <v-alert v-if="previousSessions.length === 0" type="info">
              {{ $t('account.noPreviousSessions') }}
            </v-alert>

            <v-list v-else class="account-sessions__list px-2" lines="three">
              <v-list-item
                v-for="session in previousSessions"
                :key="session.sessionId"
                class="account-sessions__item px-4 py-3"
              >
                <template v-slot:prepend>
                  <v-icon class="mr-2 mt-2">{{ getDeviceIcon(session) }}</v-icon>
                </template>

                <v-list-item-title>
                  {{ getDeviceName(session) }}
                </v-list-item-title>

                <v-list-item-subtitle>
                  <v-row density="comfortable">
                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionIpAddress') }}:</strong>
                      {{ session.ipAddress || $t('account.sessionUnknown') }}
                    </v-col>

                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionFirstSeen') }}:</strong>
                      {{ formatTimestamp(session.firstSeenAt) }}
                    </v-col>

                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionLastSeen') }}:</strong>
                      {{ formatTimestamp(session.lastSeenAt) }}
                    </v-col>

                    <v-col cols="12" sm="6" lg="3">
                      <strong>{{ $t('account.sessionExpires') }}:</strong>
                      {{ formatTimestamp(session.expiresAt) }}
                    </v-col>
                  </v-row>
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="isConfirmDialogOpen" max-width="520" persistent>
      <v-card>
        <v-card-title class="pa-3">{{ confirmDialogTitle }}</v-card-title>
        <v-card-text>{{ confirmDialogMessage }}</v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" :disabled="isConfirming" @click="closeConfirmDialog">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn variant="flat" color="red-darken-3" :loading="isConfirming" @click="confirmTermination">
            {{ $t('account.terminateSession') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/api/Api'

export default {
  name: "AccountSessions",
  data: function() {
    return {
      sessions: [],
      isLoading: false,
      isTerminatingOthers: false,
      terminatingSessionId: null,
      errorMessage: null,
      confirmAction: null,
      selectedSession: null,
      isConfirmDialogOpen: false,
    }
  },
  computed: {
    isActionDisabled: function() {
      return this.isLoading || this.isTerminatingOthers || !!this.terminatingSessionId;
    },
    isConfirming: function() {
      return this.isTerminatingOthers || !!this.terminatingSessionId;
    },
    hasSessions: function() {
      return this.sessions.length > 0;
    },
    activeSessions: function() {
      return this.sessions.filter((session) => !session.invalidatedAt);
    },
    previousSessions: function() {
      return this.sessions.filter((session) => session.invalidatedAt !== null).slice(0, 10);
    },
    terminableOtherSessionCount: function() {
      return this.sessions.filter((session) => !session.invalidatedAt).length;
    },
    confirmDialogTitle: function() {
      if (this.confirmAction === 'terminateOthers') {
        return this.$t('account.confirmTerminateOtherSessionsTitle');
      }

      return this.$t('account.confirmTerminateSessionTitle');
    },
    confirmDialogMessage: function() {
      if (this.confirmAction === 'terminateOthers') {
        return this.$t('account.confirmTerminateOtherSessionsMessage');
      }

      return this.$t('account.confirmTerminateSessionMessage');
    },
  },
  mounted: function() {
    this.loadSessions();
  },
  methods: {
    async loadSessions() {
      this.isLoading = true;
      this.errorMessage = null;

      try {
        const response = await Api.user.getSessions();
        this.sessions = Array.isArray(response?.data?.items) ? response.data.items : [];
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },
    openTerminateDialog(session) {
      if (session.isCurrent) {
        return;
      }

      this.selectedSession = session;
      this.confirmAction = 'terminateSession';
      this.isConfirmDialogOpen = true;
    },
    openTerminateOthersDialog() {
      this.selectedSession = null;
      this.confirmAction = 'terminateOthers';
      this.isConfirmDialogOpen = true;
    },
    closeConfirmDialog() {
      if (this.isConfirming) {
        return;
      }

      this.isConfirmDialogOpen = false;
      this.confirmAction = null;
      this.selectedSession = null;
    },
    async confirmTermination() {
      if (this.confirmAction === 'terminateOthers') {
        await this.terminateOtherSessions();
        return;
      }

      if (this.selectedSession) {
        await this.terminateSession(this.selectedSession.sessionId);
      }
    },
    async terminateSession(sessionId) {
      this.terminatingSessionId = sessionId;
      this.errorMessage = null;

      try {
        await Api.user.terminateSession(sessionId);
        this.$showMessage(this.$t('account.sessionTerminated'), { type: 'success' });
        this.closeConfirmDialogAfterSuccess();
        await this.loadSessions();
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.terminatingSessionId = null;
      }
    },
    async terminateOtherSessions() {
      this.isTerminatingOthers = true;
      this.errorMessage = null;

      try {
        await Api.user.terminateOtherSessions();
        this.$showMessage(this.$t('account.otherSessionsTerminated'), { type: 'success' });
        this.closeConfirmDialogAfterSuccess();
        await this.loadSessions();
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.isTerminatingOthers = false;
      }
    },
    closeConfirmDialogAfterSuccess() {
      this.isConfirmDialogOpen = false;
      this.confirmAction = null;
      this.selectedSession = null;
    },
    getErrorMessage(error) {
      if (error?.response?.data?.error) {
        if (error.response.data.error.code === 1016 /* Session invalidated */) {
          return this.$t('account.sessionInvalidatedMessage');
        }

        return this.$localizeMessage(
          error.response.data.error,
          error.response.data.error.message || this.$t('account.sessionsApiError')
        );
      }

      return error?.message || this.$t('account.sessionsApiError');
    },
    getDeviceName(session) {
      const device = session?.device || {};
      const browserName = device.browser?.name || '';
      const browser = [device.browser?.name, device.browser?.version].filter(Boolean).join(' ');
      const os = [device.os?.name, device.os?.version].filter(Boolean).join(' ');
      const model = device.model || '';

      if ((browserName === 'TubeWay Mobile' || browserName === 'TubeWay Desktop') && os) {
        return model ? `${browserName} on ${model} (${os})` : `${browserName} on ${os}`;
      }

      const deviceParts = [
        browser,
      ].filter(Boolean);

      if (device.deviceType === 'mobile' && model) {
        deviceParts.push(model, os);
      } else if (device.deviceType) {
        deviceParts.push(os);
        deviceParts.push(device.deviceType);
      }

      if (deviceParts.length > 0) {
        return deviceParts.join(' · ');
      }

      return session?.clientType || this.$t('account.sessionUnknownDevice');
    },
    getDeviceIcon(session) {
      const device = session?.device?.deviceType || '';
      const userAgent = session?.userAgent || '';
      const deviceText = `${device} ${userAgent}`.toLowerCase();

      if (deviceText.includes('mobile') || deviceText.includes('phone')) {
        return 'mdi-cellphone';
      }

      if (deviceText.includes('tablet')) {
        return 'mdi-tablet';
      }

      return 'mdi-monitor';
    },
    formatTimestamp(timestamp) {
      if (!timestamp) {
        return this.$t('account.sessionUnknown');
      }

      const timestampValue = Number(timestamp);
      const normalizedTimestamp = Number.isFinite(timestampValue) && timestampValue < 1000000000000
        ? timestampValue * 1000
        : timestamp;
      const date = new Date(normalizedTimestamp);

      if (Number.isNaN(date.getTime())) {
        return this.$t('account.sessionUnknown');
      }

      return new Intl.DateTimeFormat(this.$i18n.locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
    },
  },
}
</script>

<style lang="scss" scoped>
@layer app {
  .account-sessions__loading {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .account-sessions__list {
    background: transparent;
  }

  .account-sessions__item {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .account-sessions__item--current {
    border-color: rgba(var(--v-theme-primary), 0.7);
    box-shadow: inset 3px 0 0 rgb(var(--v-theme-primary));
  }

  .gap-2 {
    gap: 8px;
  }

  .v-list-item-title {
    line-height: 2;
  }
}
</style>
